import type { EFile } from '@tabservice/tabfile-client';
import { isInFlight } from '@tabservice/tabfile-client';
import { ChevronRight } from 'lucide-react';
import { SortableTH, Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import type { SortState } from '@/components/ui/table';
import { FilingStatusPill } from './status-pill';
import { filingKindLabel } from './status';
import { filingFormatTag } from '@/features/new-filing/formats';
import { formatCount, formatDate } from '@/lib/format';
import { cn } from '@/lib/cn';

function sentAsLabel(filing: EFile): string {
  const format = filing.format ?? (filing.source === 'json' ? 'json' : undefined);
  if (!format) return '-';
  return filingFormatTag[format] ?? format.toUpperCase();
}

/**
 * The web application generates an idempotency key when it files on somebody's behalf, and
 * that is a uuid with `web-` in front of it. It is a real key and it belongs in the API, but
 * it is not a reference anybody chose, so it is not shown as one.
 */
function customerReference(filing: EFile): string | null {
  const key = filing.idempotencyKey;
  if (!key || key.startsWith('web-')) return null;
  return key;
}

/**
 * What this e-file is called, in the order a customer would recognise it: the name of the
 * file they sent, then the reference their own system set, then nothing.
 *
 * Nothing is the honest answer for records pasted in as JSON with no reference: an e-file id
 * already sits in the column to the left, and repeating it here would fill the column without
 * telling anybody anything.
 */
export function filingFileLabel(filing: EFile): string | null {
  return filing.fileName ?? customerReference(filing);
}

/**
 * Enough of the e-file id to tell two rows apart and to read out over the phone. The whole id
 * is on the e-file's own page, next to a button that copies it.
 */
function shortId(id: string): string {
  return id.includes('-') ? id.slice(0, 8) : id;
}

/**
 * One page of e-files, as Tab Service returned it. Searching, filtering, ordering and paging
 * all happen on the server so that a page means the same thing however many e-files there
 * are: sorting the ten rows on screen would put the oldest of this page above the newest of
 * the next one and call it sorted.
 *
 * There is no tax year column. The year is chosen in the header and every row on the page has
 * it, so a column of it would be the same value nine times.
 *
 * The Kind column carries the e-file an amendment is against, because "Correction" on its own
 * does not say correction of what, and a customer reading a list of six e-files for one form
 * has no way to pair them up. The id is a link to that e-file.
 */
export function FilingsTable({
  items,
  busy,
  sort,
  onSort,
  onOpen,
}: {
  items: EFile[];
  busy?: boolean;
  sort: SortState | null;
  onSort: (key: string) => void;
  onOpen: (filingId: string) => void;
}) {
  const rows = items;

  return (
    <>
      <TableWrap data-tour="e-files-table" busy={busy}>
        <Table>
          <THead>
            <tr>
              <TH>E-file</TH>
              <SortableTH columnKey="file" sort={sort} onSort={onSort}>
                File
              </SortableTH>
              <SortableTH columnKey="form" sort={sort} onSort={onSort}>
                Form
              </SortableTH>
              <TH>Kind</TH>
              <SortableTH columnKey="status" sort={sort} onSort={onSort}>
                Status
              </SortableTH>
              <SortableTH columnKey="records" sort={sort} onSort={onSort} align="right">
                Records
              </SortableTH>
              <SortableTH columnKey="accepted" sort={sort} onSort={onSort} align="right">
                Accepted
              </SortableTH>
              <SortableTH columnKey="created" sort={sort} onSort={onSort}>
                Created
              </SortableTH>
              <TH className="w-10" aria-label="Open" />
            </tr>
          </THead>
          <TBody>
            {rows.map((filing) => {
              const label = filingFileLabel(filing);
              // The IRS has not answered yet, so nothing has been accepted and nothing has
              // been refused. A nought here reads as "every record failed", which is the one
              // thing it does not mean.
              const awaiting = isInFlight(filing.status);
              return (
                <TR
                  key={filing.id}
                  className="group"
                  interactive
                  tabIndex={0}
                  role="link"
                  aria-label={`Open e-file ${label ?? filing.id}`}
                  onClick={() => onOpen(filing.id)}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onOpen(filing.id);
                    }
                  }}
                >
                  <TD className="font-mono text-sm text-muted-foreground" title={filing.id}>
                    {shortId(filing.id)}
                  </TD>
                  <TD className="max-w-[320px]">
                    {label ? (
                      <span className="block truncate font-medium" title={label}>
                        {label}
                      </span>
                    ) : (
                      <span className="text-muted-foreground" title="Records were sent to the API, not uploaded as a file">
                        &mdash;
                      </span>
                    )}
                    <span className="block text-xs text-muted-foreground">{sentAsLabel(filing)}</span>
                  </TD>
                  {/* 1099-NEC is one word to a reader, and a hyphen is a line break to a table. */}
                  <TD className="font-medium whitespace-nowrap">{filing.formType}</TD>
                  <TD className="text-muted-foreground">
                    <span className="block">{filingKindLabel[filing.kind]}</span>
                    {filing.parentEfileId ? (
                      <button
                        type="button"
                        className={cn(
                          'mt-0.5 block font-mono text-xs text-primary',
                          'hover:underline focus-visible:underline focus-visible:outline-none',
                        )}
                        title={`Open e-file ${filing.parentEfileId}, which this ${filingKindLabel[
                          filing.kind
                        ].toLowerCase()} amends`}
                        // The row itself opens this e-file. This opens the one it amends, so the
                        // click must not reach the row underneath it.
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpen(filing.parentEfileId as string);
                        }}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        of {shortId(filing.parentEfileId)}
                      </button>
                    ) : null}
                  </TD>
                  <TD>
                    <FilingStatusPill status={filing.status} />
                  </TD>
                  <TD numeric>{formatCount(filing.recordCount)}</TD>
                  <TD numeric className="text-muted-foreground">
                    {awaiting ? (
                      <span title="The IRS has not answered yet">&mdash;</span>
                    ) : (
                      formatCount(filing.acceptedCount ?? 0)
                    )}
                  </TD>
                  <TD className="whitespace-nowrap text-muted-foreground">{formatDate(filing.createdAt)}</TD>
                  {/* The arrow only appears under the pointer, so the table stays quiet at rest. */}
                  <TD className="pl-0 text-right">
                    <ChevronRight
                      className={cn(
                        'ml-auto h-4 w-4 text-primary opacity-0 transition-opacity duration-150 ease-out',
                        'group-hover:opacity-100 group-focus-visible:opacity-100',
                      )}
                      aria-hidden="true"
                    />
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      </TableWrap>
    </>
  );
}
